import React from 'react';
import { Card, CardContent, Typography, Box, Grid, Stack } from '@mui/material';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js';
import { PAYMENT_METHOD } from "../../enums/payment-method.enum";

// Register required Chart.js elements
ChartJS.register(ArcElement, Tooltip);

// Default color palette for payment methods
const DEFAULT_COLORS = {
    Credit: '#3B82F6',
    Cash: '#10B981',
    'Mobile Money Payment': '#8B5CF6',
};

const FALLBACK_COLORS = ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444'];

export default function PaymentMethodGauge({
    title = 'Ecommerce Order Payment Method Trend',
    subtitle = '',
    data = {}, // Accepts object like { Credit: { count: 536, total: 90696716 }, Cash: ... }
    valueKey = 'count', // 'count' or 'total' to control chart gauge ratios
    customColors = {},
    percentageInParentheses = false,
}) {
    // Helper function to parse item values safely
    const parseItemData = (itemData) => {
        if (typeof itemData === 'number') {
            return { count: itemData, total: 0 };
        }
        if (itemData && typeof itemData === 'object') {
            return {
                count: Number(itemData.count ?? itemData.value ?? 0),
                total: Number(itemData.total ?? 0),
            };
        }
        return { count: 0, total: 0 };
    };

    // Extract dynamic keys/labels from data object
    const keys = Object.keys(data || {});

    // Format data list for legend and chart display
    const formattedData = keys.map((key, index) => {
        const parsed = parseItemData(data[key]);
        const chartValue = valueKey === 'total' ? parsed.total : parsed.count;
        const color = customColors[key] || DEFAULT_COLORS[key] || FALLBACK_COLORS[index % FALLBACK_COLORS.length];

        return {
            id: key,
            label: PAYMENT_METHOD[key],
            color,
            count: parsed.count,
            totalAmount: parsed.total,
            value: Number(chartValue) || 0,
        };
    });

    // Aggregate totals across all methods
    const totalChartValue = formattedData.reduce((acc, item) => acc + item.value, 0);

    // Chart.js Data Configuration
    const chartData = {
        labels: formattedData.map((d) => d.label),
        datasets: [
            {
                data: formattedData.length > 0 ? formattedData.map((d) => d.value) : [1],
                backgroundColor: formattedData.length > 0 ? formattedData.map((d) => d.color) : ['#E5E7EB'],
                borderWidth: 0,
                borderRadius: 4,
            },
        ],
    };

    // Chart.js Options for Semi-Circle Gauge
    const chartOptions = {
        rotation: -90,      // Start from left side
        circumference: 180, // Render as semi-circle
        cutout: '75%',      // Ring thickness
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                enabled: formattedData.length > 0,
                callbacks: {
                    label: function (context) {
                        const val = context.raw || 0;
                        const pct = totalChartValue > 0 ? ((val / totalChartValue) * 100).toFixed(1) : '0.0';
                        return ` ${context.label}: ${val.toLocaleString()} (${pct}%)`;
                    },
                },
            },
        },
    };

    return (
        <Card
            sx={{
                borderRadius: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <CardContent
                sx={{
                    p: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    '&:last-child': { pb: 3 },
                }}
            >
                {/* Header Title & Subtitle */}
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 3 }}>
                    <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, fontSize: '1.125rem' }}
                    >
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>

                <Grid container spacing={3} alignItems="center" sx={{ flexGrow: 1 }}>
                    {/* Left Breakdown / Legend List */}
                    <Grid item xs={12} sm={7}>
                        <Stack spacing={2}>
                            {formattedData.map((item) => {
                                const percentage = totalChartValue > 0 
                                    ? ((item.value / totalChartValue) * 100).toFixed(1) 
                                    : '0.0';

                                return (
                                    <Box
                                        key={item.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1
                                        }}
                                    >
                                        {/* Color Dot + Label */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 100 }}>
                                            <Box
                                                sx={{
                                                    width: 12,
                                                    height: 12,
                                                    borderRadius: percentageInParentheses ? '50%' : '3px',
                                                    bgcolor: item.color,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {item.label}
                                            </Typography>
                                        </Box>

                                        {/* Values Breakdown */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, textAlign: 'right' }}>
                                            {/* Count */}
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                {item.count.toLocaleString()}
                                            </Typography>

                                            {/* Total monetary amount if available */}
                                            {item.totalAmount > 0 && (
                                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, marginTop: 0 }}>
                                                    ({item.totalAmount.toLocaleString()} Ks)
                                                </Typography>
                                            )}

                                            {/* Percentage */}
                                            <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600, minWidth: 45, marginTop: 0 }}>
                                                {percentageInParentheses ? `(${percentage}%)` : `${percentage}%`}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Grid>

                    {/* Right Half-Gauge Chart */}
                    <Grid item xs={12} sm={5}>
                        <Box
                            sx={{
                                position: 'relative',
                                width: 170,
                                height: 90,
                                mx: 'auto',
                            }}
                        >
                            <Doughnut data={chartData} options={chartOptions} />
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}